import React, { useState, useMemo, useCallback } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  setMonth,
  setYear,
  format,
  getYear,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import styles from './DateInput.module.scss';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const YEARS_PER_PAGE = 12;

interface CalendarPanelProps {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  initialMonth?: Date;
}

export const CalendarPanel: React.FC<CalendarPanelProps> = ({ selectedDate, onSelect, initialMonth }) => {
  const [currentMonth, setCurrentMonth] = useState(initialMonth || selectedDate || new Date());
  const [view, setView] = useState<'days' | 'months' | 'years'>('days');
  const [yearsPageStart, setYearsPageStart] = useState(() => {
    const y = getYear(initialMonth || selectedDate || new Date());
    return y - (y % YEARS_PER_PAGE);
  });

  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const currentYear = getYear(currentMonth);
  const thisYear = new Date().getFullYear();

  const handlePrevMonth = useCallback(() => setCurrentMonth(prev => subMonths(prev, 1)), []);
  const handleNextMonth = useCallback(() => setCurrentMonth(prev => addMonths(prev, 1)), []);

  const handlePrevYear = useCallback(() => {
    setCurrentMonth(prev => setYear(prev, getYear(prev) - 1));
  }, []);

  const handleNextYear = useCallback(() => {
    setCurrentMonth(prev => setYear(prev, getYear(prev) + 1));
  }, []);

  const handleMonthSelect = useCallback((monthIndex: number) => {
    setCurrentMonth(prev => setMonth(prev, monthIndex));
    setView('days');
  }, []);

  const handleYearSelect = useCallback((year: number) => {
    setCurrentMonth(prev => setYear(prev, year));
    setView('months');
  }, []);

  const yearsOnPage = useMemo(
    () => Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearsPageStart + i),
    [yearsPageStart],
  );

  // --- Years view ---
  if (view === 'years') {
    return (
      <div className={styles.calendar}>
        <div className={styles.calendarHeader}>
          <button type="button" className={styles.navButton} onClick={() => setYearsPageStart(p => p - YEARS_PER_PAGE)}>
            <FiChevronLeft size={16} />
          </button>
          <span className={styles.headerTitleStatic}>
            {yearsPageStart} – {yearsPageStart + YEARS_PER_PAGE - 1}
          </span>
          <button type="button" className={styles.navButton} onClick={() => setYearsPageStart(p => p + YEARS_PER_PAGE)}>
            <FiChevronRight size={16} />
          </button>
        </div>
        <div className={styles.yearsGrid}>
          {yearsOnPage.map(year => (
            <button
              type="button"
              key={year}
              className={`${styles.yearCell} ${year === currentYear ? styles.yearCellSelected : ''} ${year === thisYear ? styles.yearCellCurrent : ''}`}
              onClick={() => handleYearSelect(year)}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- Months view ---
  if (view === 'months') {
    return (
      <div className={styles.calendar}>
        <div className={styles.calendarHeader}>
          <button type="button" className={styles.navButton} onClick={handlePrevYear}>
            <FiChevronLeft size={16} />
          </button>
          <button
            type="button"
            className={styles.headerTitle}
            onClick={() => {
              setYearsPageStart(currentYear - (currentYear % YEARS_PER_PAGE));
              setView('years');
            }}
          >
            {currentYear}
          </button>
          <button type="button" className={styles.navButton} onClick={handleNextYear}>
            <FiChevronRight size={16} />
          </button>
        </div>
        <div className={styles.monthsGrid}>
          {MONTHS.map((month, index) => (
            <button
              type="button"
              key={month}
              className={`${styles.monthCell} ${index === currentMonth.getMonth() ? styles.monthCellSelected : ''}`}
              onClick={() => handleMonthSelect(index)}
            >
              {month.slice(0, 3)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- Days view ---
  return (
    <div className={styles.calendar}>
      <div className={styles.calendarHeader}>
        <button type="button" className={styles.navButton} onClick={handlePrevMonth}>
          <FiChevronLeft size={16} />
        </button>
        <button type="button" className={styles.headerTitle} onClick={() => setView('months')}>
          {format(currentMonth, 'LLLL yyyy', { locale: ru })}
        </button>
        <button type="button" className={styles.navButton} onClick={handleNextMonth}>
          <FiChevronRight size={16} />
        </button>
      </div>

      <div className={styles.weekdayRow}>
        {WEEKDAYS.map(day => (
          <div key={day} className={styles.weekdayCell}>
            {day}
          </div>
        ))}
      </div>

      <div className={styles.daysGrid}>
        {days.map(day => {
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isCurrent = isToday(day);
          const isOtherMonth = !isSameMonth(day, currentMonth);

          return (
            <button
              type="button"
              key={day.toISOString()}
              className={`${styles.dayCell} ${isSelected ? styles.dayCellSelected : ''} ${isCurrent ? styles.dayCellToday : ''} ${isOtherMonth ? styles.dayCellMuted : ''}`}
              onClick={() => onSelect(day)}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
};
